// src/pages/rapim/RapimCreate.tsx — MODERN REDESIGN
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Save, Calendar, Users, FileText,
  MapPin, Search, Upload, X, Paperclip, Gavel, AlertTriangle, Info, Check,
  Building2, Star} from 'lucide-react';
import {
  Button, Card, Input, Textarea, Breadcrumb, Loading} from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { rapimService } from '../../services/rapim';
import { User } from '../../types';

// ========== TYPES ==========
interface PesertaItem {
  id: number; name: string; role: string; roleName: string;
  unit?: string; selected: boolean;
}
interface LaporanItem {
  id: number; kode: string; judul: string; unit: string;
  risiko: string; selected: boolean; rapim_ulang?: boolean;
  riwayat_keputusan?: any;
}

// ========== STEPPER CONFIG ==========
const STEPS = [
  { id: 1, title: 'Informasi Rapat', subtitle: 'Judul, tanggal & tempat', icon: Calendar },
  { id: 2, title: 'Peserta', subtitle: 'Pilih undangan rapat', icon: Users },
  { id: 3, title: 'Agenda Laporan', subtitle: 'Opsional, pilih bila ada', icon: FileText },
  { id: 4, title: 'Lampiran', subtitle: 'Surat undangan & dokumen', icon: Paperclip },
];

// ========== ROLE GROUPING ==========
const ROLE_GROUPS = [
  { label: 'Pimpinan', roles: ['rektor', 'sekretaris'] },
  { label: 'Wakil Rektor', roles: ['wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4'] },
  { label: 'Dekan', roles: ['dekan'] },
  { label: 'Kepala Unit', roles: ['kalem', 'kaprodi', 'kabag', 'ka_tu'] },
  { label: 'Lainnya', roles: ['lpm', 'senat', 'admin', 'staff', 'kalab'] },
];

const RISIKO_COLORS: Record<string, string> = {
  rendah: 'bg-green-100 text-green-700 border-green-200',
  sedang: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  tinggi: 'bg-orange-100 text-orange-700 border-orange-200',
  kritis: 'bg-red-100 text-red-700 border-red-200',
  critical: 'bg-red-100 text-red-700 border-red-200'};

// ========== MAIN COMPONENT ==========
const RapimCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    judul: '', tanggal: '', waktu: '09:00',
    tempat: '', jenis: '' as '' | 'rutin' | 'insidental' | 'darurat',
    agenda: ''});
  const [peserta, setPeserta] = useState<PesertaItem[]>([]);
  const [laporan, setLaporan] = useState<LaporanItem[]>([]);
  const [suratUndanganFile, setSuratUndanganFile] = useState<File | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Search & filter state
  const [pesertaSearch, setPesertaSearch] = useState('');
  const [laporanSearch, setLaporanSearch] = useState('');
  const [pesertaGroupFilter, setPesertaGroupFilter] = useState<string>('semua');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ========== LOAD DATA ==========
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [usersRes, laporanRes] = await Promise.all([
          adminService.users.getAll({ per_page: 200 }),
          rapimService.getAvailableLaporan(),
        ]);

        const usersData = (usersRes as any)?.data || usersRes;
        const laporanData = (laporanRes as any)?.data || laporanRes;

        const pItems: PesertaItem[] = (Array.isArray(usersData) ? usersData : usersData?.data || [])
          .filter((u: User) => u.is_active)
          .map((u: User) => ({
            id: u.id, name: u.name,
            role: u.role?.display_name || u.role?.name || 'User',
            roleName: u.role?.name || '',
            unit: u.unit?.name || '',
            selected: false}));
        setPeserta(pItems);

        const lItems: LaporanItem[] = (Array.isArray(laporanData) ? laporanData : laporanData?.data || [])
          .map((l: any) => ({
            id: l.id, kode: l.kode, judul: l.judul,
            unit: l.unit?.name || '-', risiko: l.tingkat_risiko,
            selected: false, rapim_ulang: Boolean(l.rapim_ulang),
            riwayat_keputusan: l.riwayat_keputusan || null}));
        setLaporan(lItems);
      } catch (e) {
        toast.error('Gagal memuat data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ========== COMPUTED ==========
  const selectedPesertaCount = peserta.filter(p => p.selected).length;
  const selectedLaporanCount = laporan.filter(l => l.selected).length;
  const rapimUlangCount = laporan.filter(l => l.rapim_ulang).length;
  const totalDokumenCount = pendingFiles.length + (suratUndanganFile ? 1 : 0);

  const filteredPeserta = useMemo(() => {
    return peserta.filter(p => {
      const matchSearch = !pesertaSearch
        || p.name.toLowerCase().includes(pesertaSearch.toLowerCase())
        || p.role.toLowerCase().includes(pesertaSearch.toLowerCase())
        || (p.unit || '').toLowerCase().includes(pesertaSearch.toLowerCase());

      const matchGroup = pesertaGroupFilter === 'semua'
        || ROLE_GROUPS.find(g => g.label === pesertaGroupFilter)?.roles.includes(p.roleName);

      return matchSearch && matchGroup;
    });
  }, [peserta, pesertaSearch, pesertaGroupFilter]);

  const filteredLaporan = useMemo(() => {
    const filtered = laporan.filter(l =>
      !laporanSearch
      || l.judul.toLowerCase().includes(laporanSearch.toLowerCase())
      || l.kode.toLowerCase().includes(laporanSearch.toLowerCase())
    );
    return filtered.sort((a, b) => Number(Boolean(b.rapim_ulang)) - Number(Boolean(a.rapim_ulang)));
  }, [laporan, laporanSearch]);

  // ========== HANDLERS ==========
  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const togglePeserta = (id: number) => {
    setPeserta(prev => prev.map(p => p.id === id ? { ...p, selected: !p.selected } : p));
  };

  const toggleLaporan = (id: number) => {
    setLaporan(prev => prev.map(l => l.id === id ? { ...l, selected: !l.selected } : l));
  };

  const selectAllPeserta = () => {
    const ids = new Set(filteredPeserta.map(p => p.id));
    const allSelected = filteredPeserta.every(p => p.selected);
    setPeserta(prev => prev.map(p => ids.has(p.id) ? { ...p, selected: !allSelected } : p));
  };

  const collectValidFiles = (files: FileList | File[]) => {
    const valid: File[] = [];
    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} > 10MB`); return; }
      valid.push(file);
    });
    return valid;
  };

  const handleSuratUndanganSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const [file] = collectValidFiles(files);
    if (file) setSuratUndanganFile(file);
    e.target.value = '';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const valid = collectValidFiles(files);
    setPendingFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ========== VALIDATION ==========
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.judul.trim()) errs.judul = 'Judul wajib diisi';
    if (!formData.tanggal) errs.tanggal = 'Tanggal wajib diisi';
    if (!formData.jenis) errs.jenis = 'Jenis rapat wajib dipilih';
    if (selectedPesertaCount === 0) errs.peserta = 'Minimal 1 peserta';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ========== SUBMIT ==========
  const handleSave = async () => {
    if (!validate()) {
      toast.error('Harap lengkapi semua field wajib');
      if (!formData.judul || !formData.tanggal || !formData.jenis) setCurrentStep(1);
      else if (selectedPesertaCount === 0) setCurrentStep(2);
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('judul', formData.judul.trim());
      payload.append('tanggal', `${formData.tanggal} ${formData.waktu}:00`);
      payload.append('jenis', formData.jenis);

      if (formData.tempat.trim()) payload.append('tempat', formData.tempat.trim());
      if (formData.agenda.trim()) payload.append('agenda', formData.agenda.trim());

      peserta
        .filter(p => p.selected)
        .forEach(p => payload.append('peserta_ids[]', String(p.id)));

      laporan
        .filter(l => l.selected)
        .forEach(l => payload.append('laporan_ids[]', String(l.id)));

      if (suratUndanganFile) {
        payload.append('surat_undangan_file', suratUndanganFile);
      }

      pendingFiles.forEach(file => payload.append('lampiran_files[]', file));

      await rapimService.create(payload);

      toast.success('Rapat berhasil dijadwalkan!');
      navigate('/rapim');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal menyimpan rapat');
    } finally {
      setSaving(false);
    }
  };

  // ========== STEP NAVIGATION ==========
  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  // ========== LOADING STATE ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'RAPIM', href: '/rapim' },
        { label: 'Buat Rapat Baru' },
      ]} />

      {/* ===== HEADER ===== */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Gavel className="w-6 h-6 text-teal-main" />
            Buat Rapat Pimpinan
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Jadwalkan rapat baru dan tentukan agenda pembahasan
          </p>
        </div>
      </div>

      {/* ===== VISUAL STEPPER ===== */}
      <div className="relative">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const isActive = currentStep === step.id;
            const isComplete = currentStep > step.id;
            const Icon = step.icon;

            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className="flex flex-col items-center gap-2 relative z-10 group"
                >
                  <div className={`
                    w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300
                    ${isActive
                      ? 'bg-teal-main text-white shadow-lg shadow-teal-main/30 scale-110'
                      : isComplete
                        ? 'bg-success text-white'
                        : 'bg-neutral-bg text-neutral-text-secondary group-hover:bg-neutral-border'
                    }
                  `}>
                    {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="text-center">
                    <p className={`text-xs font-bold ${isActive ? 'text-teal-main' : isComplete ? 'text-success' : 'text-neutral-text-secondary'
                      }`}>
                      {step.title}
                    </p>
                    <p className="text-[10px] text-neutral-text-secondary hidden sm:block">
                      {step.id === 2 && selectedPesertaCount > 0 ? `${selectedPesertaCount} dipilih` :
                        step.id === 3 && selectedLaporanCount > 0 ? `${selectedLaporanCount} dipilih` :
                          step.id === 4 && totalDokumenCount > 0 ? `${totalDokumenCount} file` :
                            step.subtitle}
                    </p>
                  </div>
                </button>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 rounded transition-colors duration-300 ${currentStep > step.id ? 'bg-success' : 'bg-neutral-border'
                    }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== STEP CONTENT ===== */}
      <Card className="min-h-[400px]">

        {/* STEP 1: Informasi Rapat */}
        {currentStep === 1 && (
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-teal-main/10">
                <Calendar className="w-6 h-6 text-teal-main" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-teal-dark">Informasi Rapat</h2>
                <p className="text-xs text-neutral-text-secondary">Detail dasar rapat pimpinan</p>
              </div>
            </div>

            <Input
              label="Judul Rapat"
              placeholder="Contoh: RAPIM Rutin Minggu ke-5 Januari"
              value={formData.judul}
              onChange={(e) => updateField('judul', e.target.value)}
              error={errors.judul}
              required
            />

            {/* Jenis Rapat — Visual Cards */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Jenis Rapat <span className="text-danger">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'rutin', label: 'Rutin', desc: 'Rapat terjadwal berkala', emoji: '📅', color: 'border-teal-main bg-teal-50' },
                  { value: 'insidental', label: 'Insidental', desc: 'Rapat khusus', emoji: '⚡', color: 'border-amber-400 bg-amber-50' },
                  { value: 'darurat', label: 'Darurat', desc: 'Rapat mendesak', emoji: '🚨', color: 'border-red-400 bg-red-50' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => updateField('jenis', opt.value)}
                    className={`
                      p-4 rounded-xl border-2 text-left transition-all
                      ${formData.jenis === opt.value
                        ? `${opt.color} shadow-md scale-[1.02]`
                        : 'border-neutral-border bg-white hover:border-neutral-text-secondary'
                      }
                    `}
                  >
                    <span className="text-2xl">{opt.emoji}</span>
                    <p className="text-sm font-bold mt-2">{opt.label}</p>
                    <p className="text-[10px] text-neutral-text-secondary mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
              {errors.jenis && <p className="text-xs text-danger">{errors.jenis}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Tanggal" type="date" value={formData.tanggal}
                onChange={(e) => updateField('tanggal', e.target.value)}
                error={errors.tanggal} required min={new Date().toISOString().split('T')[0]} />
              <Input label="Waktu" type="time" value={formData.waktu}
                onChange={(e) => updateField('waktu', e.target.value)} />
            </div>

            <Input label="Tempat" placeholder="Ruang Rapat Rektor Lt.3 / Link Zoom"
              value={formData.tempat} onChange={(e) => updateField('tempat', e.target.value)}
              icon={<MapPin className="w-4 h-4" />} />

            <Textarea label="Agenda (opsional)" placeholder="Tuliskan agenda rapat..."
              value={formData.agenda} onChange={(e) => updateField('agenda', e.target.value)}
              rows={4} helpText="Agenda terstruktur bisa ditambahkan setelah rapat dibuat" />
          </div>
        )}

        {/* STEP 2: Peserta */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-teal-dark">Undang Peserta</h2>
                  <p className="text-xs text-neutral-text-secondary">
                    {selectedPesertaCount} dari {peserta.length} dipilih
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={selectAllPeserta}>
                {filteredPeserta.every(p => p.selected) ? 'Batal Semua' : 'Pilih Semua'}
              </Button>
            </div>

            {errors.peserta && (
              <div className="p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-danger" />
                <p className="text-xs text-danger font-medium">{errors.peserta}</p>
              </div>
            )}

            {/* Search + Group Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
                <input type="text" placeholder="Cari nama, role, atau unit..."
                  value={pesertaSearch} onChange={(e) => setPesertaSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-light" />
              </div>
              <div className="flex items-center gap-1 p-1 bg-neutral-bg rounded-xl overflow-x-auto shrink-0">
                {['semua', ...ROLE_GROUPS.map(g => g.label)].map(group => (
                  <button key={group} onClick={() => setPesertaGroupFilter(group)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${pesertaGroupFilter === group
                      ? 'bg-white text-teal-main shadow-sm' : 'text-neutral-text-secondary'
                      }`}>
                    {group === 'semua' ? 'Semua' : group}
                  </button>
                ))}
              </div>
            </div>

            {/* Peserta Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto pr-1">
              {filteredPeserta.map((p) => (
                <label
                  key={p.id}
                  className={`
                    flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all
                    ${p.selected
                      ? 'bg-teal-main/5 border-2 border-teal-main/30 shadow-sm'
                      : 'bg-white border-2 border-transparent hover:bg-neutral-bg'
                    }
                  `}
                >
                  <input type="checkbox" checked={p.selected} onChange={() => togglePeserta(p.id)}
                    className="rounded border-neutral-border text-teal-main focus:ring-teal-light" />
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-white shrink-0 ${p.selected ? 'bg-teal-main' : 'bg-neutral-text-secondary'
                    }`}>
                    {p.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    <p className="text-[10px] text-neutral-text-secondary truncate">
                      {p.role} {p.unit ? `• ${p.unit}` : ''}
                    </p>
                  </div>
                  {p.selected && <Check className="w-4 h-4 text-teal-main shrink-0" />}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3: Agenda Laporan */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-100">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-teal-dark">Agenda Laporan (Opsional)</h2>
                <p className="text-xs text-neutral-text-secondary">
                  {selectedLaporanCount} laporan dipilih
                  {rapimUlangCount > 0 && ` • ${rapimUlangCount} RAPIM ulang`}
                </p>
              </div>
            </div>

            <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-start gap-2">
              <Info className="w-4 h-4 text-info shrink-0 mt-0.5" />
              <p className="text-xs text-info">
                Langkah ini opsional. Rapat tetap bisa dibuat tanpa memilih agenda laporan,
                dan laporan dapat ditambahkan setelah rapat tersimpan.
              </p>
            </div>

            {rapimUlangCount > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-700">
                  <strong>{rapimUlangCount} laporan</strong> dikembalikan dari tindak lanjut — ditampilkan lebih atas.
                </p>
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input type="text" placeholder="Cari kode atau judul laporan..."
                value={laporanSearch} onChange={(e) => setLaporanSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-light" />
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {filteredLaporan.map((l) => (
                <label key={l.id} className={`
                  flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all
                  ${l.selected
                    ? 'bg-teal-main/5 border-2 border-teal-main/30 shadow-sm'
                    : l.rapim_ulang
                      ? 'bg-amber-50 border-2 border-amber-200 hover:border-amber-300'
                      : 'bg-white border-2 border-transparent hover:bg-neutral-bg'
                  }
                `}>
                  <input type="checkbox" checked={l.selected} onChange={() => toggleLaporan(l.id)}
                    className="mt-1 rounded border-neutral-border text-teal-main focus:ring-teal-light" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-teal-main font-bold">{l.kode}</span>
                      {l.rapim_ulang && (
                        <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-[10px] font-bold rounded-full uppercase">
                          🔄 RAPIM Ulang
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${RISIKO_COLORS[l.risiko] || RISIKO_COLORS.sedang
                        }`}>
                        {l.risiko?.charAt(0).toUpperCase() + l.risiko?.slice(1) || 'Sedang'}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-text-primary">{l.judul}</p>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">
                      <Building2 className="w-3 h-3 inline mr-1" />{l.unit}
                    </p>
                    {l.rapim_ulang && l.riwayat_keputusan && (
                      <div className="mt-2 text-[10px] text-amber-700 bg-white/50 rounded-lg p-2 border border-amber-200">
                        Progres terakhir: {l.riwayat_keputusan?.progress_terakhir ?? 0}% —
                        Alasan: {l.riwayat_keputusan?.alasan_kembali || '-'}
                      </div>
                    )}
                  </div>
                  {l.selected && <Check className="w-5 h-5 text-teal-main shrink-0 mt-1" />}
                </label>
              ))}
              {filteredLaporan.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-neutral-border" />
                  <p className="text-sm text-neutral-text-secondary">Tidak ada laporan tersedia</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 4: Lampiran */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-orange-100">
                <Paperclip className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-teal-dark">Lampiran Undangan</h2>
                <p className="text-xs text-neutral-text-secondary">
                  {totalDokumenCount} file siap dikirim bersama email undangan (opsional)
                </p>
              </div>
            </div>

            <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-start gap-2">
              <Info className="w-4 h-4 text-info shrink-0 mt-0.5" />
              <p className="text-xs text-info">
                File yang diunggah di langkah ini akan ikut dilampirkan saat email undangan dikirim ke peserta rapat.
              </p>
            </div>

            <div className="space-y-3 rounded-2xl border border-neutral-border/60 p-4">
              <div>
                <h3 className="text-sm font-bold text-teal-dark">Surat Undangan Rapat</h3>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Unggah 1 file surat undangan resmi bila tersedia.
                </p>
              </div>

              <div className="border-2 border-dashed border-neutral-border rounded-2xl p-6 text-center
                hover:border-teal-light hover:bg-teal-main/5 transition-all cursor-pointer"
                onClick={() => document.getElementById('surat-undangan-upload')?.click()}
              >
                <input type="file" id="surat-undangan-upload" onChange={handleSuratUndanganSelect} className="hidden" />
                <Upload className="w-10 h-10 text-neutral-text-secondary mx-auto mb-3" />
                <p className="text-sm font-semibold text-neutral-text-primary">
                  Klik untuk memilih surat undangan
                </p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  PDF, DOC, DOCX, JPG, PNG (maks. 10MB)
                </p>
              </div>

              {suratUndanganFile && (
                <div className="flex items-center justify-between p-3 bg-neutral-bg rounded-xl">
                  <div className="flex items-center gap-3">
                    <Paperclip className="w-4 h-4 text-neutral-text-secondary" />
                    <div>
                      <p className="text-sm font-medium">{suratUndanganFile.name}</p>
                      <p className="text-xs text-neutral-text-secondary">
                        Surat undangan | {formatFileSize(suratUndanganFile.size)}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setSuratUndanganFile(null)}
                    className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-bold text-teal-dark">Dokumen Pendukung</h3>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Tambahkan bahan rapat, lampiran laporan, atau dokumen persiapan lainnya.
              </p>
            </div>

            {/* Drop Zone */}
            <div className="border-2 border-dashed border-neutral-border rounded-2xl p-8 text-center
              hover:border-teal-light hover:bg-teal-main/5 transition-all cursor-pointer"
              onClick={() => document.getElementById('file-upload-create')?.click()}
            >
              <input type="file" id="file-upload-create" multiple onChange={handleFileSelect} className="hidden" />
              <Upload className="w-12 h-12 text-neutral-text-secondary mx-auto mb-3" />
              <p className="text-sm font-semibold text-neutral-text-primary">
                Klik untuk memilih file
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1">
                PDF, DOC, XLS, PPT, JPG, PNG, ZIP (maks. 10MB)
              </p>
            </div>

            {/* File List */}
            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                {pendingFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-neutral-bg rounded-xl">
                    <div className="flex items-center gap-3">
                      <Paperclip className="w-4 h-4 text-neutral-text-secondary" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-neutral-text-secondary">
                          Dokumen pendukung | {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== index))}
                      className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Summary Preview */}
            <Card className="!bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
              <h3 className="text-sm font-bold text-teal-dark mb-3 flex items-center gap-2">
                <Star className="w-4 h-4" /> Ringkasan Rapat
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-white/70 rounded-xl text-center">
                  <p className="text-lg font-black text-teal-main">{formData.judul ? 'Ya' : '-'}</p>
                  <p className="text-[10px] text-neutral-text-secondary">Judul</p>
                </div>
                <div className="p-3 bg-white/70 rounded-xl text-center">
                  <p className="text-lg font-black text-teal-main">{selectedPesertaCount}</p>
                  <p className="text-[10px] text-neutral-text-secondary">Peserta</p>
                </div>
                <div className="p-3 bg-white/70 rounded-xl text-center">
                  <p className="text-lg font-black text-teal-main">{selectedLaporanCount}</p>
                  <p className="text-[10px] text-neutral-text-secondary">Laporan</p>
                </div>
                <div className="p-3 bg-white/70 rounded-xl text-center">
                  <p className="text-lg font-black text-teal-main">{totalDokumenCount}</p>
                  <p className="text-[10px] text-neutral-text-secondary">Dokumen</p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Card>

      {/* ===== FOOTER ACTIONS ===== */}
      <div className="flex items-center justify-between">
        {currentStep > 1 ? (
          <Button variant="outline" onClick={prevStep} icon={<ArrowLeft className="w-4 h-4" />}>
            Sebelumnya
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate('/rapim')} icon={<ArrowLeft className="w-4 h-4" />}>
            Batal
          </Button>
        )}

        <div className="flex items-center gap-3">
          {currentStep < 4 ? (
            <Button variant="primary" onClick={nextStep} icon={<ArrowRight className="w-4 h-4" />}>
              Lanjut
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              icon={<Save className="w-4 h-4" />}
              className="!px-8"
            >
              Simpan & Jadwalkan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RapimCreate;
